// Package imports:
import React, { useState } from 'react';
// Component imports:
import DisplayBox from '../../../ui-elements/DisplayBox/DisplayBox';
import Table from '../../../ui-elements/Table/Table';
import Radio from '../../../ui-elements/Radio/Radio';
import Input from '../../../ui-elements/Input/Input';
import Checkbox from '../../../ui-elements/Input/Checkbox';
import Button from '../../../ui-elements/Button/Button';
// import Select from '../../../ui-elements/Select/Select';
import { insertDash } from '../../../services/utils';
import DropSelectSearch from '../../../ui-elements/DropSelect/DropSelectSearch/DropSelectSearch';
import { CountryList } from '../../../assets/staticData/CountryList';
import Tooltip from '../../../ui-elements/Tooltip/Tooltip';
import PoliticalDangerExample from './PoliticalDangerExample';
import CountryConnections from './CountryConnections';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';

/**
 * CompanyKYCExample
 * 
 * A static example/preview component that demonstrates the layout and structure
 * of a Company KYC (Know Your Customer) due diligence questionnaire.
 * 
 * This is NOT a fully interactive form — text inputs are disabled.
 * However, radio button selections ARE functional and toggle the visibility
 * of conditional fields and sub-questions to demonstrate the intended UX flow.
 * 
 * The Radio component works by comparing its `value` prop against `defaultValue`.
 * When `value === defaultValue`, the radio appears checked. The `onChange` callback
 * fires on mouse down and returns the selected value, which we store in state
 * and pass back as `defaultValue` to all radios in the same group.
 * 
 * Questions covered:
 *  1. Tax residency (conditional country dropdown when "Nei")
 *  2. Cash-intensive business (conditional sub-questions when "Já")
 *  3. Source of funds (conditional text input when "Annað")
 *  4. Business with high-risk/non-cooperative countries (conditional fields when "Já")
 *  5. Purpose of intended business relationship (always visible input)
 *  6. Transactions on behalf of third party (conditional input when "Já")
 *  7. PEP status (conditional detail fields when "Já", plus sub-questions 7b/7c)
 *  8. PEP status of other company directors (conditional person entry when "Já")
 *  9. Beneficial owners confirmation (read-only table)
 * 10. Company registry information confirmation (read-only tables)
 */
const CompanyKYCExample: React.FC = () => {
    // =====================================================================
    // STATE — Radio / Dropdown selections
    // Each question with conditional content gets its own state variable.
    // The value stored here is the string that matches the Radio `value` prop.
    // An empty string "" means no selection has been made yet.
    //
    // For yes/no questions we use "true"/"false" strings to match Radio values.
    // For multi-option questions (like Q3) we use the option identifiers.
    // =====================================================================

    /** Q1: Tax domicile in Iceland? "false" reveals country dropdown */
    const [q1, setQ1] = useState<string>("");

    /** Q2: Cash-intensive business? "true" reveals sub-questions */
    const [q2, setQ2] = useState<string>("");

    /** Q2a: Cash percentage of revenue (sub-question, no conditional content) */
    const [q2a, setQ2a] = useState<string>("");

    /** Q2b: Clear cash accounting processes? (sub-question, no conditional content) */
    const [q2b, setQ2b] = useState<string>("");

    /** Q3: Source of funds. "opt4" (Annað/Other) reveals text input */
    const [q3, setQ3] = useState<string>("");

    /** Q4: Business with high-risk countries? "true" reveals country + explanation fields */
    const [q4, setQ4] = useState<string>("");

    /** Q6: Transactions on behalf of third party? "true" reveals text input */
    const [q6, setQ6] = useState<string>("");

    /** Q7: PEP status? "true" reveals position/relationship/deposits fields */
    const [q7, setQ7] = useState<string>("");

    /** Q7b: Regular foreign payments? "true" reveals purpose text input */
    const [q7b, setQ7b] = useState<string>("");

    /** Q7c: Regular cash usage with XXXX? "true" reveals purpose text input */
    const [q7c, setQ7c] = useState<string>("");

    /** Q8: Other directors PEP? "true" reveals person entry fields */
    const [q8, setQ8] = useState<string>("");

    /** Q9: Confirm beneficial owners (no conditional content) */
    const [q9, setQ9] = useState<string>("");

    /** Q10: Confirm company registry info (no conditional content) */
    const [q10, setQ10] = useState<string>("");

    // =====================================================================
    // STATE — Text input values
    // Each text input gets its own state variable so the component is fully
    // controlled and values can be collected on submit.
    // =====================================================================

    /** Q2b conditional: Cash accounting explanation */
    const [q2bTextValue, setQ2bTextValue] = useState<string>("");

    /** Q3 conditional: Source of funds explanation (when "Annað" selected) */
    const [q3TextValue, setQ3TextValue] = useState<string>("");

    /** Q5: Purpose of business relationship */
    const [q5Value, setQ5Value] = useState<string>("");

    /** Q6 conditional: Third party explanation (when "Já" selected) */
    const [q6TextValue, setQ6TextValue] = useState<string>("");

    /** Q7 conditional: PEP position */
    const [q7PositionValue, setQ7PositionValue] = useState<string>("");

    /** Q7 conditional: PEP relationship */
    const [q7RelationshipValue, setQ7RelationshipValue] = useState<string>("");

    /** Q7 conditional: PEP deposits source */
    const [q7DepositsValue, setQ7DepositsValue] = useState<string>("");

    /** Q7b conditional: Foreign payments purpose */
    const [q7bTextValue, setQ7bTextValue] = useState<string>("");

    /** Q7c conditional: Cash usage purpose */
    const [q7cTextValue, setQ7cTextValue] = useState<string>("");

    /** Q9 conditional: Reason for not confirming beneficial owners */
    const [q9TextValue, setQ9TextValue] = useState<string>("");

    /** Q10 conditional: Reason for not confirming registry info */
    const [q10TextValue, setQ10TextValue] = useState<string>("");

    const [checkboxStatus, setCheckboxStatus] = React.useState(false)

    return (
        <div className="kyc-example">
            <h1>Áreiðanleikakönnun</h1>

            {/* Company info header showing basic company details */}
            <div className="KCL_company-title">
                <div className="info-col">
                    <div className="info-item">
                        <span className="title">Fyrirtæki</span>
                        <span className="value companyName">Dæmi ehf.</span>
                    </div>
                </div>
                <div className="info-col">
                    <div className="info-item">
                        <span className="title">Kennitala</span>
                        <span className="value">451267-3489</span>
                    </div>
                </div>
                <div className="info-col">
                    <div className="info-item">
                        <span className="title">Póstfang</span>
                        <span className="value">Borgartúni 25, 105 Reykjavík</span>
                    </div>
                </div>
            </div>

            <DisplayBox displayBoxSize="xl" grayBackground>
                <ol className="kyc-questions">
                    {/* ============================================================
                        QUESTION 1: Tax Residency
                        Asks whether the company's tax domicile is in Iceland.
                        If "Nei" (No) is selected, a country dropdown appears
                        so the user can specify which country.
                    ============================================================ */}
                    <li>
                        <p className="paragraph--bold">
                            Er skattaleg heimilisfesti félagsins á Íslandi?
                        </p>
                        <div className="radios-group">
                            <ul>
                                <li>
                                    <Radio
                                        id="q1-yes"
                                        name="q1"
                                        value="true"
                                        defaultValue={q1}
                                        onChange={() => setQ1("true")}
                                    >
                                        Já
                                    </Radio>
                                </li>
                                <li>
                                    <Radio
                                        id="q1-no"
                                        name="q1"
                                        value="false"
                                        defaultValue={q1}
                                        onChange={() => setQ1("false")}
                                    >
                                        Nei
                                    </Radio>
                                </li>
                            </ul>
                        </div>
                        {/* Conditional dropdown: only visible when "Nei" is selected */}
                        {q1 === "false" && (
                            <div className="conditional-field">
                                <DropSelectSearch
                                    options={CountryList}
                                    label="Tilgreinið land"
                                    size='lg'
                                />
                            </div>
                        )}
                    </li>

                    {/* ============================================================
                        QUESTION 2: Cash-Intensive Business
                        Asks whether the company deals with significant amounts
                        of cash. When "Já" (Yes) is selected, two sub-questions
                        appear:
                          a) What percentage of revenue is in cash
                          b) Whether the company has clear cash accounting processes
                    ============================================================ */}
                    <li>
                        <p className="paragraph--bold">
                            Stundar félagið viðskipti með mikið reiðufé?
                        </p>
                        <div className="radios-group">
                            <ul>
                                <li>
                                    <Radio
                                        id="q2-yes"
                                        name="q2"
                                        value="true"
                                        defaultValue={q2}
                                        onChange={() => setQ2("true")}
                                    >
                                        Já
                                    </Radio>
                                </li>
                                <li>
                                    <Radio
                                        id="q2-no"
                                        name="q2"
                                        value="false"
                                        defaultValue={q2}
                                        onChange={() => setQ2("false")}
                                    >
                                        Nei
                                    </Radio>
                                </li>
                            </ul>
                        </div>

                        {/* Sub-questions only appear when "Já" is selected */}
                        {q2 === "true" && (
                            <>
                                {/* Sub-question 2a: Cash percentage of total revenue */}
                                <div className="sub-question">
                                    <p className="paragraph-small--bold">
                                        Hversu stór hluti viðskipta félagsins felur í sér reiðufé, miðað við heildarveltu?
                                    </p>
                                    <div className="radios-group">
                                        <ul>
                                            <li>
                                                <Radio
                                                    id="q2a-opt1"
                                                    name="q2a"
                                                    value="opt1"
                                                    defaultValue={q2a}
                                                    onChange={() => setQ2a("opt1")}
                                                >
                                                    Minna en 5%
                                                </Radio>
                                            </li>
                                            <li>
                                                <Radio
                                                    id="q2a-opt2"
                                                    name="q2a"
                                                    value="opt2"
                                                    defaultValue={q2a}
                                                    onChange={() => setQ2a("opt2")}
                                                >
                                                    5 til 20%
                                                </Radio>
                                            </li>
                                            <li>
                                                <Radio
                                                    id="q2a-opt3"
                                                    name="q2a"
                                                    value="opt3"
                                                    defaultValue={q2a}
                                                    onChange={() => setQ2a("opt3")}
                                                >
                                                    Meira en 20%
                                                </Radio>
                                            </li>
                                        </ul>
                                    </div>
                                </div>

                                {/* Sub-question 2b: Clear cash accounting processes */}
                                <div className="sub-question">
                                    <p className="paragraph-small--bold">
                                        Hefur félagið skýra ferla eða bókhald yfir inn- og útgreiðslur í reiðufé?
                                    </p>
                                    <div className="radios-group">
                                        <ul>
                                            <li>
                                                <Radio
                                                    id="q2b-yes"
                                                    name="q2b"
                                                    value="true"
                                                    defaultValue={q2b}
                                                    onChange={() => setQ2b("true")}
                                                >
                                                    Já
                                                </Radio>
                                            </li>
                                            <li>
                                                <Radio
                                                    id="q2b-no"
                                                    name="q2b"
                                                    value="false"
                                                    defaultValue={q2b}
                                                    onChange={() => setQ2b("false")}
                                                >
                                                    Nei
                                                </Radio>
                                            </li>
                                        </ul>
                                    </div>
                                    {/* Text input for further explanation about cash processes */}
                                    <Input
                                        label="Vinsamlegast útskýrið nánar"
                                        inputSize="lg"
                                        value={q2bTextValue}
                                        onChange={(e) => setQ2bTextValue(e.target.value)}
                                    />
                                </div>
                            </>
                        )}
                    </li>

                    {/* ============================================================
                        QUESTION 3: Source of Funds
                        Asks the user to specify the origin of funds used for
                        transactions with the service provider (XXXX).
                        Options include capital income/wages/dividends, operating
                        revenue, loans/investment, or "Other".
                        When "Annað" (Other / opt4) is selected, a text input appears.
                    ============================================================ */}
                    <li>
                        <p className="paragraph--bold">
                            Tilgreindu uppruna þeirra fjármuna sem notaðir eru til viðskipta við XXXX.
                        </p>
                        <div className="radios-group">
                            <ul>
                                <li>
                                    <Radio
                                        id="q3-opt1"
                                        name="q3"
                                        value="opt1"
                                        defaultValue={q3}
                                        onChange={() => setQ3("opt1")}
                                    >
                                        Fjármagnstekjuskattur, laun, arður
                                    </Radio>
                                </li>
                                <li>
                                    <Radio
                                        id="q3-opt2"
                                        name="q3"
                                        value="opt2"
                                        defaultValue={q3}
                                        onChange={() => setQ3("opt2")}
                                    >
                                        Rekstratekjur
                                    </Radio>
                                </li>
                                <li>
                                    <Radio
                                        id="q3-opt3"
                                        name="q3"
                                        value="opt3"
                                        defaultValue={q3}
                                        onChange={() => setQ3("opt3")}
                                    >
                                        Lánsfé eða fjárfesting
                                    </Radio>
                                </li>
                                <li>
                                    <Radio
                                        id="q3-opt4"
                                        name="q3"
                                        value="opt4"
                                        defaultValue={q3}
                                        onChange={() => setQ3("opt4")}
                                    >
                                        Annað
                                    </Radio>
                                </li>
                            </ul>
                        </div>
                        {/* Conditional text input: only visible when "Annað" (opt4) is selected */}
                        {q3 === "opt4" && (
                            <Input
                                className='inputThatNeedsMargin'
                                label="Vinsamlegast gerðu grein fyrir uppruna fjármuna"
                                inputSize="lg"
                                value={q3TextValue}
                                onChange={(e) => setQ3TextValue(e.target.value)}
                            />
                        )}
                    </li>

                    {/* ============================================================
                        QUESTION 4: Business with High-Risk / Non-Cooperative Countries
                        Asks whether the company does business with countries on
                        the high-risk or non-cooperative list (FATF).
                        Includes an info icon (tooltip) linking to the list.
                        When "Já" (Yes) is selected, a country dropdown and text
                        explanation field appear, along with an "Add" button
                        to add multiple country entries.
                    ============================================================ */}
                    <li>
                        <div className="question-with-info">
                            <p className="paragraph--bold">
                                Stundar félagið viðskipti við ríki sem tilgreind eru á lista yfir áhættusöm eða ósamvinnuþýð ríki?
                            </p>
                            <Tooltip
                                tooltipClassName="viewCompanyAnswers-tooltip"
                                noUnderline
                                tooltip={<div className="tooltip-text">
                                    Listi yfir áhættusöm og ósamvinnuþýð ríki sýnir annars vegar ríki sem Financial Action Task Force (FATF) hefur tilgreint sem áhættusöm og eftir atvikum ósamvinnuþýð ríki og hins vegar ríki sem Evrópusambandið hefur tilgreint sem áhættusöm þriðju lönd (reglugerð nr. 105/2020).
                                </div>}
                                text={<FontAwesomeIcon icon={faInfoCircle} />}
                            />
                        </div>
                        <div className="radios-group">
                            <ul>
                                <li>
                                    <Radio
                                        id="q4-yes"
                                        name="q4"
                                        value="true"
                                        defaultValue={q4}
                                        onChange={() => setQ4("true")}
                                    >
                                        Já
                                    </Radio>
                                </li>
                                <li>
                                    <Radio
                                        id="q4-no"
                                        name="q4"
                                        value="false"
                                        defaultValue={q4}
                                        onChange={() => setQ4("false")}
                                    >
                                        Nei
                                    </Radio>
                                </li>
                            </ul>
                        </div>
                        {/* Conditional fields: only visible when "Já" is selected */}
                        {q4 === "true" && (
                            <CountryConnections />
                        )}
                    </li>

                    {/* ============================================================
                        QUESTION 5: Purpose of Intended Business
                        Asks the user to describe the purpose of the intended
                        business relationship — i.e., what service or solution
                        they intend to use from XXXX.
                        Shows as a text input with a floating label.
                        This field is always visible (no conditional logic).
                    ============================================================ */}
                    <li>
                        <p className="paragraph--bold">
                            Hver er tilgangur fyrirhugaðra viðskipta, það er hvaða þjónustu eða lausn hyggst félagið nýta hjá XXXX?
                        </p>
                        <Input
                            className='inputThatNeedsMargin'
                            label="Tilgangur viðskipta"
                            inputSize="lg"
                            value={q5Value}
                            onChange={(e) => setQ5Value(e.target.value)}
                        />
                    </li>

                    {/* ============================================================
                        QUESTION 6: Transactions on Behalf of Third Party
                        Asks whether the service being sought is on behalf of
                        a third party rather than the company or person filling
                        out the questionnaire.
                        When "Já" (Yes) is selected, a text input appears asking
                        to identify the third party and the nature of the relationship.
                    ============================================================ */}
                    <li>
                        <p className="paragraph--bold">
                            Eru viðskiptin í þágu þriðja aðila?
                        </p>
                        <p className="paragraph-small">
                            Er sú þjónusta eða lausn sem þú sækist eftir vegna þriðja aðila, en ekki þín eða félags sem þú ert í forsvari fyrir?
                        </p>
                        <div className="radios-group">
                            <ul>
                                <li>
                                    <Radio
                                        id="q6-yes"
                                        name="q6"
                                        value="true"
                                        defaultValue={q6}
                                        onChange={() => setQ6("true")}
                                    >
                                        Já
                                    </Radio>
                                </li>
                                <li>
                                    <Radio
                                        id="q6-no"
                                        name="q6"
                                        value="false"
                                        defaultValue={q6}
                                        onChange={() => setQ6("false")}
                                    >
                                        Nei
                                    </Radio>
                                </li>
                            </ul>
                        </div>
                        {/* Conditional text input: only visible when "Já" is selected */}
                        {q6 === "true" && (
                            <Input
                                className='inputThatNeedsMargin'
                                label="Hvaða aðila og hvert er eðli sambandsins?"
                                inputSize="lg"
                                value={q6TextValue}
                                onChange={(e) => setQ6TextValue(e.target.value)}
                            />
                        )}
                    </li>

                    {/* ============================================================
                        QUESTION 7: PEP (Politically Exposed Person) Status
                        A multi-part question that asks:
                          a) Whether the user, a close family member, or associate
                             holds or has held a high-ranking public position
                          b) If yes: which position, what relationship, and source
                             of account deposits
                          c) Whether the user regularly receives or sends foreign
                             payments (to/from Iceland)
                          d) Whether the user regularly uses cash in their
                             transactions with XXXX
                        
                        Sub-questions 7b and 7c become visible once Q7 has any
                        selection, since they are separate follow-up questions
                        within the same numbered item. Their own text inputs
                        only show when their respective "Já" is selected.
                    ============================================================ */}
                    <li>
                        {/* Main PEP question with info icon */}
                        <div className="question-with-info">
                            <p className="paragraph--bold">
                                Stjórnmálaleg tengsl: Ert þú, náinn fjölskyldumeðlimur eða samstarfsmaður háttsettur í opinberri þjónustu?
                            </p>
                            <Tooltip
                                tooltipClassName="viewCompanyAnswers-tooltip"
                                noUnderline
                                tooltip={<div className="tooltip-text">
                                    Til einstaklinga í áhættuhópi vegna stjórnmálalegra tengsla teljast þeir einstaklingar, innlendir og erlendir, sem eru eða hafa verið háttsettir í opinberri þjónustu, ásamt nánustu fjölskyldu þeirra og nánum samstarfsmönnum. Seðlabanki Íslands heldur úti lista yfir starfsheiti sem teljast til háttsettra opinberra starfa.
                                </div>}
                                text={<FontAwesomeIcon icon={faInfoCircle} />}
                            />
                        </div>
                        <div className="radios-group">
                            <ul>
                                <li>
                                    <Radio
                                        id="q7-yes"
                                        name="q7"
                                        value="true"
                                        defaultValue={q7}
                                        onChange={() => setQ7("true")}
                                    >
                                        Já
                                    </Radio>
                                </li>
                                <li>
                                    <Radio
                                        id="q7-no"
                                        name="q7"
                                        value="false"
                                        defaultValue={q7}
                                        onChange={() => setQ7("false")}
                                    >
                                        Nei
                                    </Radio>
                                </li>
                            </ul>
                        </div>

                        {/* Conditional fields when PEP = "Já": position, relationship, deposits source */}
                        {q7 === "true" && (
                            <div className="conditional-field question7">
                                <Input
                                    label="Hvaða háttsetta starf/störf?"
                                    inputSize="lg"
                                    value={q7PositionValue}
                                    onChange={(e) => setQ7PositionValue(e.target.value)}
                                />
                                <Input
                                    label="Hver eru tengslin?"
                                    inputSize="lg"
                                    value={q7RelationshipValue}
                                    onChange={(e) => setQ7RelationshipValue(e.target.value)}
                                />
                                <Input
                                    label="Hvaðan berast innborganir inná reikninga þína?"
                                    inputSize="lg"
                                    value={q7DepositsValue}
                                    onChange={(e) => setQ7DepositsValue(e.target.value)}
                                />
                            </div>
                        )}

                        {/* Sub-question 7b: Regular foreign payments
                            Visible once Q7 has been answered (regardless of yes/no),
                            since this is a separate follow-up within the same question group */}
                        {q7 === "true" && (
                            <div className="sub-question">
                                <p className="paragraph-small--bold">
                                    Móttekur þú eða sendir reglulega út erlendar greiðslur (til og frá Íslandi)?
                                </p>
                                <div className="radios-group">
                                    <ul>
                                        <li>
                                            <Radio
                                                id="q7b-yes"
                                                name="q7b"
                                                value="true"
                                                defaultValue={q7b}
                                                onChange={() => setQ7b("true")}
                                            >
                                                Já
                                            </Radio>
                                        </li>
                                        <li>
                                            <Radio
                                                id="q7b-no"
                                                name="q7b"
                                                value="false"
                                                defaultValue={q7b}
                                                onChange={() => setQ7b("false")}
                                            >
                                                Nei
                                            </Radio>
                                        </li>
                                    </ul>
                                </div>
                                {/* Purpose text input: only visible when Q7b = "Já" */}
                                {q7b === "true" && (
                                    <Input
                                        label="Í hvaða tilgangi?"
                                        inputSize="lg"
                                        value={q7bTextValue}
                                        onChange={(e) => setQ7bTextValue(e.target.value)}
                                    />
                                )}
                            </div>
                        )}

                        {/* Sub-question 7c: Regular cash usage with XXXX
                            Also visible once Q7 has been answered */}
                        {q7 === "true" && (
                            <div className="sub-question">
                                <p className="paragraph-small--bold">
                                    Notar þú reglulega reiðufé í þínum viðskiptum við XXXX?
                                </p>
                                <div className="radios-group">
                                    <ul>
                                        <li>
                                            <Radio
                                                id="q7c-yes"
                                                name="q7c"
                                                value="true"
                                                defaultValue={q7c}
                                                onChange={() => setQ7c("true")}
                                            >
                                                Já
                                            </Radio>
                                        </li>
                                        <li>
                                            <Radio
                                                id="q7c-no"
                                                name="q7c"
                                                value="false"
                                                defaultValue={q7c}
                                                onChange={() => setQ7c("false")}
                                            >
                                                Nei
                                            </Radio>
                                        </li>
                                    </ul>
                                </div>
                                {/* Purpose text input: only visible when Q7c = "Já" */}
                                {q7c === "true" && (
                                    <Input
                                        label="Í hvaða tilgangi?"
                                        inputSize="lg"
                                        value={q7cTextValue}
                                        onChange={(e) => setQ7cTextValue(e.target.value)}
                                    />
                                )}
                            </div>
                        )}
                    </li>

                    {/* ============================================================
                        QUESTION 8: PEP Status of Other Company Directors
                        Asks whether any directors, or those authorized to bind
                        the company, their close family or associates have held
                        high-ranking public positions in the past 12 months.
                        
                        When "Já" is selected, a row of inputs appears for:
                        - Kennitala (national ID)
                        - Nafn (name, auto-filled/disabled based on kennitala lookup)
                        - Háttsett starf (high-ranking position held)
                        Plus a checkbox for "Erlendur aðili" (foreign party).
                        An "Add" button allows adding multiple entries.
                    ============================================================ */}
                    <li>
                        <p className="paragraph--bold">
                            Aðrir stjórnendur félagsins
                        </p>
                        <p className="paragraph-small">
                            Hefur einhver af stjórnendum félagsins eða aðrir sem hafa heimild til að skuldbinda félagið, þeirra nánasta fjölskylda eða nánir samstarfsmenn verið háttsettir í opinberri þjónustu síðastliðna tólf mánuði?
                        </p>
                        <div className="radios-group">
                            <ul>
                                <li>
                                    <Radio
                                        id="q8-yes"
                                        name="q8"
                                        value="true"
                                        defaultValue={q8}
                                        onChange={() => setQ8("true")}
                                    >
                                        Já
                                    </Radio>
                                </li>
                                <li>
                                    <Radio
                                        id="q8-no"
                                        name="q8"
                                        value="false"
                                        defaultValue={q8}
                                        onChange={() => setQ8("false")}
                                    >
                                        Nei
                                    </Radio>
                                </li>
                            </ul>
                        </div>

                        {/* Conditional section: only visible when "Já" is selected */}
                        {q8 === "true" && (
                            <PoliticalDangerExample />

                        )}
                    </li>

                    {/* ============================================================
                        QUESTION 9: Beneficial Owners
                        Displays a read-only table of beneficial owners (those
                        who own or control >25% of the company, directly or
                        indirectly). Data is pre-filled from the system.
                        The user is asked to confirm whether the information
                        is correct.
                    ============================================================ */}
                    <li>
                        <p className="paragraph--bold">
                            Raunverulegir eigendur
                        </p>
                        <p className="paragraph-small">
                            Raunverulegur eigandi telst sá einstaklingur sem á eða ræður yfir &gt;25% hlut í félaginu, beint eða óbeint.
                        </p>
                        <div className="company-info-section">
                            <Table
                                tableSize="lg"
                                data={[{
                                    name: "Kelda Kóðadóttir",
                                    birthMonth: "1979/10",
                                    ownedPercent: "54.30%"
                                }]}
                                columns={[{
                                    title: "Nafn",
                                    renderCell: ({ name }) => name,
                                    textAlign: "left"
                                }, {
                                    title: "Fæðingarár/mán.",
                                    renderCell: ({ birthMonth }) => birthMonth,
                                    textAlign: "left"
                                }, {
                                    title: "Eignarhlutur",
                                    renderCell: ({ ownedPercent }) => ownedPercent,
                                    textAlign: "right"
                                }]}
                            />
                        </div>
                        <p className="paragraph-small--bold">
                            Staðfesta raunverulega eigendur?
                        </p>
                        <div className="radios-group">
                            <ul>
                                <li>
                                    <Radio
                                        id="q9-yes"
                                        name="q9"
                                        value="true"
                                        defaultValue={q9}
                                        onChange={() => setQ9("true")}
                                    >
                                        Já
                                    </Radio>
                                </li>
                                <li>
                                    <Radio
                                        id="q9-no"
                                        name="q9"
                                        value="false"
                                        defaultValue={q9}
                                        onChange={() => setQ9("false")}
                                    >
                                        Nei
                                    </Radio>
                                </li>
                            </ul>
                        </div>
                        {/* Conditional section: only visible when "Nei" is selected */}
                        {q9 === "false" && (
                            <div className="conditional-field">
                                <Input
                                    label="Ástæða"
                                    inputSize="lg"
                                    value={q9TextValue}
                                    onChange={(e) => setQ9TextValue(e.target.value)}
                                />
                            </div>
                        )}
                    </li>

                    {/* ============================================================
                        QUESTION 10: Company Registry Information
                        Displays multiple read-only data tables pulled from the
                        Icelandic company registry (Fyrirtækjaskrá):
                          - Stjórn félags (Board of Directors)
                          - Endurskoðendur (Auditors)
                          - Framkvæmdastjórn (Executive Management)
                          - Prókúruumboð (Procuration Authority)
                          - Stofnendur (Founders)
                          - Tilgangur (Company Purpose)
                          - ÍSAT flokkun (ISAT Industry Classification)
                        
                        The user is asked to confirm whether this registry
                        information is correct.
                    ============================================================ */}
                    <li>
                        <p className="paragraph--bold">
                            Upplýsingar úr fyrirtækjaskrá
                        </p>
                        <div className="company-info-section">
                            {/* Board of Directors table */}
                            <div className="company-info-subsection">
                                <h5>Stjórn félags</h5>
                                <Table
                                    tableSize="lg"
                                    data={[{
                                        ssn: "190189-2349",
                                        name: "Jón Kelddal",
                                        address: "Kelduvogur 20",
                                        role: "Meðstjórnandi"
                                    }, {
                                        ssn: "010101-0100",
                                        name: "Sýnir Dæmason",
                                        address: "Hvergigata 4",
                                        role: "Meðstjórnandi"
                                    }, {
                                        ssn: "170691-1239",
                                        name: "Dæmi Sýnis",
                                        address: "Hvergigata 5",
                                        role: "Stjórnarformaður"
                                    }]}
                                    columns={[{
                                        title: "Kennitala",
                                        renderCell: ({ ssn }) => insertDash(ssn),
                                        textAlign: "left"
                                    }, {
                                        title: "Nafn",
                                        renderCell: ({ name }) => name,
                                        textAlign: "left"
                                    }, {
                                        title: "Heimilisfang",
                                        renderCell: ({ address }) => address,
                                        textAlign: "left"
                                    }, {
                                        title: "Staða",
                                        renderCell: ({ role }) => role,
                                        textAlign: "left"
                                    }]}
                                />
                            </div>

                            {/* Auditors table */}
                            <div className="company-info-subsection">
                                <h5>Endurskoðendur</h5>
                                <Table
                                    tableSize="lg"
                                    data={[{
                                        ssn: "210987-6549",
                                        name: "Dæmhildur Sýnmundsdóttir",
                                        address: "Keldugata 1"
                                    }, {
                                        ssn: "560178-2349",
                                        name: "Endurskoðendadæmi ehf.",
                                        address: "Endurskoðendavegur 4"
                                    }]}
                                    columns={[{
                                        title: "Kennitala",
                                        renderCell: ({ ssn }) => insertDash(ssn),
                                        textAlign: "left"
                                    }, {
                                        title: "Nafn",
                                        renderCell: ({ name }) => name,
                                        textAlign: "left"
                                    }, {
                                        title: "Heimilisfang",
                                        renderCell: ({ address }) => address,
                                        textAlign: "left"
                                    }]}
                                />
                            </div>

                            {/* Executive Management table */}
                            <div className="company-info-subsection">
                                <h5>Framkvæmdastjórn</h5>
                                <Table
                                    tableSize="lg"
                                    data={[{
                                        ssn: "091079-1239",
                                        name: "Kelda Kóðadóttir",
                                        address: "Kelduvogur 20"
                                    }]}
                                    columns={[{
                                        title: "Kennitala",
                                        renderCell: ({ ssn }) => insertDash(ssn),
                                        textAlign: "left"
                                    }, {
                                        title: "Nafn",
                                        renderCell: ({ name }) => name,
                                        textAlign: "left"
                                    }, {
                                        title: "Heimilisfang",
                                        renderCell: ({ address }) => address,
                                        textAlign: "left"
                                    }]}
                                />
                            </div>

                            {/* Procuration Authority table */}
                            <div className="company-info-subsection">
                                <h5>Prókúruumboð</h5>
                                <Table
                                    tableSize="lg"
                                    data={[{
                                        ssn: "091079-1239",
                                        name: "Kelda Kóðadóttir",
                                        address: "Kelduvogur 20"
                                    }]}
                                    columns={[{
                                        title: "Kennitala",
                                        renderCell: ({ ssn }) => insertDash(ssn),
                                        textAlign: "left"
                                    }, {
                                        title: "Nafn",
                                        renderCell: ({ name }) => name,
                                        textAlign: "left"
                                    }, {
                                        title: "Heimilisfang",
                                        renderCell: ({ address }) => address,
                                        textAlign: "left"
                                    }]}
                                />
                            </div>

                            {/* Founders table */}
                            <div className="company-info-subsection">
                                <h5>Stofnendur</h5>
                                <Table
                                    tableSize="lg"
                                    data={[{
                                        ssn: "091079-1239",
                                        name: "Kelda Kóðadóttir",
                                        address: "Kelduvogur 20"
                                    }]}
                                    columns={[{
                                        title: "Kennitala",
                                        renderCell: ({ ssn }) => insertDash(ssn),
                                        textAlign: "left"
                                    }, {
                                        title: "Nafn",
                                        renderCell: ({ name }) => name,
                                        textAlign: "left"
                                    }, {
                                        title: "Heimilisfang",
                                        renderCell: ({ address }) => address,
                                        textAlign: "left"
                                    }]}
                                />
                            </div>

                            {/* Company Purpose description */}
                            <div className="company-info-subsection">
                                <h6>Tilgangur</h6>
                                <p className="paragraph-small">
                                    Tilgangur félagsins er hugbúnaðargerð, almenn upplýsingamiðlun, tölvuvinnsla, ráðgjöf og skyld starfsemi ásamt almennum fjárfestingum.
                                </p>
                            </div>

                            {/* ISAT Industry Classification code */}
                            <div className="company-info-subsection">
                                <h6>ÍSAT flokkun</h6>
                                <p className="paragraph-small">
                                    62.01.0 - Hugbúnaðargerð
                                </p>
                            </div>
                        </div>

                        {/* Confirmation radio for company registry information */}
                        <p className="paragraph-small--bold">
                            Staðfesta upplýsingar úr fyrirtækjaskrá?
                        </p>
                        <div className="radios-group">
                            <ul>
                                <li>
                                    <Radio
                                        id="q10-yes"
                                        name="q10"
                                        value="true"
                                        defaultValue={q10}
                                        onChange={() => setQ10("true")}
                                    >
                                        Já
                                    </Radio>
                                </li>
                                <li>
                                    <Radio
                                        id="q10-no"
                                        name="q10"
                                        value="false"
                                        defaultValue={q10}
                                        onChange={() => setQ10("false")}
                                    >
                                        Nei
                                    </Radio>
                                </li>
                            </ul>
                        </div>
                        {/* Conditional section: only visible when "Nei" is selected */}
                        {q10 === "false" && (
                            <div className="conditional-field">
                                <Input
                                    label="Ástæða"
                                    inputSize="lg"
                                    value={q10TextValue}
                                    onChange={(e) => setQ10TextValue(e.target.value)}
                                />
                            </div>
                        )}
                    </li>
                </ol>

                {/* ============================================================
                    CONFIRMATION SECTION
                    The user must check a checkbox confirming that the
                    information provided is correct to the best of their
                    knowledge, and that they commit to notifying XXXX
                    of any changes as soon as possible.
                ============================================================ */}
                <div className="confirm-info">
                    <p className="paragraph--bold">
                        Staðfesting á réttum upplýsingum
                    </p>
                    <p className="paragraph-small">
                        Ég staðfesti að þær upplýsingar sem ég hef veitt hér að ofan séu réttar eftir minni bestu vitund og skuldbind mig til að tilkynna XXXX skriflega um breytingar á þeim eins fljótt og mögulegt er.
                    </p>
                    <Checkbox
                        name="kyc-checkbox"
                        id={`kyc-checkbox-company`}
                        label={"Ég samþykki"}
                        value={(checkboxStatus || false).toString()}
                        onClick={() =>
                            setCheckboxStatus(!checkboxStatus)
                        }
                    />
                </div>

                {/* Cancel and Submit buttons */}
                <div className="actions">
                    <Button
                        disabled
                        buttonType="secondary"
                        size="lg"
                    >
                        Hætta við
                    </Button>
                    <Button
                        disabled
                        buttonType="primary"
                        size="lg"
                    >
                        Staðfesta
                    </Button>
                </div>
            </DisplayBox >
        </div >
    );
}

export default CompanyKYCExample;